import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { NavLink } from "react-router-dom";

import Moment from "react-moment";

import LocationDetails from "./LocationDetails";
import DevicesOnLocation from "./DevicesOnLocation";
import UserDetails from "./UserDetails";
import alertLabsApi from "../Api/AlertLabsApi";
import FleetWebApi from "../Api/FleetWebApi";
import { Fragment, useEffect, useMemo } from "react";
import { useState } from "react";
import { useAuthDataContext } from "../AuthDataProvider";

function SensorDetails(props) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [sensorDetails, setSensorDetails] = useState(null);
  const [lastHeard, setLastHeard] = useState(null);
  const [lastCleaningCycle, setLastCleaningCycle] = useState(null);
  const [lastReboot, setLastReboot] = useState(null);
  const [virtualSensorID, setVirtualSensorID] = useState(null);

  const { user } = useAuthDataContext();

  // Calculate if cleaning cycle is older than 2 weeks
  const cleaningCycleInfo = useMemo(() => {
    if (!lastCleaningCycle) return { isOlderThanTwoWeeks: false, shouldShowRequest: false };
    
    // If lastCleaning is null/undefined, always show request row (even if request is also null)
    if (!lastCleaningCycle.lastCleaning) {
      return {
        isOlderThanTwoWeeks: false,
        shouldShowRequest: true 
      };
    }
    
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const timestamp = new Date(lastCleaningCycle.lastCleaning);
    const isOlderThanTwoWeeks = timestamp < twoWeeksAgo;
    
    return {
      isOlderThanTwoWeeks,
      shouldShowRequest: isOlderThanTwoWeeks && !!lastCleaningCycle.lastRequest
    };
  }, [lastCleaningCycle]);

  useEffect(() => {
    const fetchSensorDetails = async () => {
      try {
        // First, check if this core ID has a virtual sensor ID
        const virtualIDs = await alertLabsApi.getVirtualSensorID(props.coreId, user.token);
        
        // Use virtual ID if it exists, otherwise use particle core ID
        const sensorId = virtualIDs?.[0] || props.coreId;
        
        // Only set virtual sensor ID if it's different from the particle core ID
        if (virtualIDs?.[0] && virtualIDs[0] !== props.coreId) {
          setVirtualSensorID(virtualIDs[0]);
        } else {
          setVirtualSensorID(null);
        }
        
        // Fetch sensor details using the appropriate ID
        const result = await alertLabsApi.getSensorDetails(sensorId, user.token);

        // Paired devices and the compound pair can come back as virtual IDs; convert them to
        // hardware (particle) IDs in one batched lookup
        const pairedIds = Array.isArray(result?.isPairedTo) ? result.isPairedTo : [];
        const compoundPairId = result?.compound?.pairedCoreID;
        const idsToResolve = compoundPairId ? [...pairedIds, compoundPairId] : pairedIds;
        if (idsToResolve.length > 0) {
          const hardwareIDs = await alertLabsApi.getHardwareIDsFromSensorIDs(
            idsToResolve,
            'particle',
            user.token
          );
          if (pairedIds.length > 0) {
            result.isPairedTo = pairedIds.map(
              (pairedId) => hardwareIDs.get(pairedId) || pairedId
            );
          }
          if (compoundPairId) {
            result.compound = {
              ...result.compound,
              pairedCoreID: hardwareIDs.get(compoundPairId) || compoundPairId,
            };
          }
        }

        setSensorDetails(result);
        setIsLoaded(true);
        setError(result?.error || null);
        // Only fetch last cleaning cycle for Shuttie devices
        if (result && (result.type === "Shuttie" || result.type === "Shuttie2")) {
          FleetWebApi.getLastCleaningCycle(props.coreId).then(
            (cycleResult) => {
              setLastCleaningCycle(cycleResult);
            },
            (error) => {
              console.log("Error fetching last cleaning cycle:", error);
            }
          );
        } else {
          setLastCleaningCycle(null);
        }
      } catch (error) {
        console.log("Error fetching sensor details:", error);
        setError(error);
        setIsLoaded(true);
      }
    };

    fetchSensorDetails();

    // Fetch last heard timestamp from Common collection
    FleetWebApi.getDeviceLastHeard(props.coreId).then(
      (result) => {
        setLastHeard(result);
      },
      (error) => {
        console.log("Error fetching last heard:", error);
      }
    );

    // Fetch last reboot timestamp (limit 1)
    FleetWebApi.getRebootData(props.coreId, 1).then(
      (result) => {
        setLastReboot(result && result.length > 0 ? result[0] : null);
      },
      (error) => {
        setLastReboot(null);
      }
    );
  }, [props.coreId, user.token]);
  let sensorDetailsContainer;
  let customDetailsContainer;
  let isPairedToContainer;
  if (error) {
    return "Error grabbing device details from the platform, Could be unregistered";
  } else if (!isLoaded) {
    return (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
  } else if (sensorDetails) {
    switch (sensorDetails?.type) {
      case "FlowieO":
      case "WaterMeter":
        customDetailsContainer = (
          <TableBody>
            {sensorDetails && sensorDetails.compound ? (
              <Fragment>
                <TableRow>
                  <TableCell component="th" scope="row">
                    Compound
                  </TableCell>
                  <TableCell data-th="Compound Setting">
                    {sensorDetails.compound.setting}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    Compound Pair
                  </TableCell>
                  <TableCell data-th="Compound Pair">
                    <NavLink
                      to={"/device/" + sensorDetails.compound.pairedCoreID}
                      className="Nav_link"
                    >
                      {sensorDetails.compound.pairedCoreID}
                    </NavLink>
                  </TableCell>
                </TableRow>
              </Fragment>
            ) : null}
            <TableRow>
              <TableCell component="th" scope="row">
                Sample Filtering
              </TableCell>
              <TableCell data-th="Sample Filtering">
                {sensorDetails.sampleFiltering}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Sample Speed
              </TableCell>
              <TableCell data-th="Sample Speed">
                {sensorDetails.samplingSpeed}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Default Unit
              </TableCell>
              <TableCell data-th="Default Unit">
                {sensorDetails.defaultUnit}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Unit
              </TableCell>
              <TableCell data-th="Unit">{sensorDetails.unit}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Axis
              </TableCell>
              <TableCell data-th="Axis">{sensorDetails.axis}</TableCell>
            </TableRow>
          </TableBody>
        );
        break;
      case "Sentree":
        customDetailsContainer = (
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                System ID
              </TableCell>
              <TableCell data-th="System ID">
                {sensorDetails.systemID}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Serial
              </TableCell>
              <TableCell data-th="Condensor Serial">
                {sensorDetails.condenser?.serial}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Brand
              </TableCell>
              <TableCell>{sensorDetails.condenser?.brand}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Model
              </TableCell>
              <TableCell data-th="Condensor Model">
                {sensorDetails.condenser?.model}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor AC Type
              </TableCell>
              <TableCell data-th="Condensor AC Type">
                {sensorDetails.condenser?.acType}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Refrigerant
              </TableCell>
              <TableCell data-th="Condensor Refrigerant">
                {sensorDetails.condenser?.refrigerant}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Metering Device Type
              </TableCell>
              <TableCell data-th="Condensor Metering Device Type">
                {sensorDetails.condenser?.meteringDeviceType}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Compressor Type
              </TableCell>
              <TableCell data-th="Condensor Compressor Type">
                {sensorDetails.condenser?.compressorType}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Tonnage
              </TableCell>
              <TableCell data-th="Condensor Tonnage">
                {sensorDetails.condenser?.tonnage}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Condensor Seer
              </TableCell>
              <TableCell data-th="Condensor Seer">
                {sensorDetails.condenser?.seer}
              </TableCell>
            </TableRow>
          </TableBody>
        );
        break;
      default:
        customDetailsContainer = null;
    }
    if (sensorDetails !== null) {
      sensorDetailsContainer = (
        <Paper>
          <Table classes={{ root: "reactivetable" }}>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  Serial Number
                </TableCell>
                <TableCell data-th="Serial Number">
                  {sensorDetails.serialNumber}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Core Id
                </TableCell>
                <TableCell data-th="Core ID">{props.coreId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Virtual Sensor ID
                </TableCell>
                <TableCell data-th="Virtual Sensor ID">
                  {virtualSensorID || 'N/A'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Device Name
                </TableCell>
                <TableCell data-th="Device Name">
                  {sensorDetails.name}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Onboarded At
                </TableCell>
                <TableCell data-th="Onboarded At">
                  {sensorDetails.createdOn ? (
                    <Moment format="YYYY-MM-DD HH:mm:ss Z">
                      {sensorDetails.createdOn}
                    </Moment>
                  ) : (
                    <div>-</div>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  First Seen
                </TableCell>
                <TableCell data-th="First Seen">
                  {sensorDetails.firstSeen ? (
                    <Moment format="YYYY-MM-DD HH:mm:ss Z">
                      {sensorDetails.firstSeen}
                    </Moment>
                  ) : (
                    <div>-</div>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Last Seen
                </TableCell>
                <TableCell data-th="Last Heard">
                  {lastHeard ? (
                    <Moment format="YYYY-MM-DD HH:mm:ss Z">
                      {lastHeard}
                    </Moment>
                  ) : (
                    <div>-</div>
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell component="th" scope="row">
                  Last Reboot
                </TableCell>
                <TableCell data-th="Last Reboot">
                  {lastReboot ? (
                    <Moment format="YYYY-MM-DD HH:mm:ss Z">
                      {lastReboot}
                    </Moment>
                  ) : (
                    <div>N/A</div>
                  )}
                </TableCell>
              </TableRow>

              {(sensorDetails.type === "Shuttie" || sensorDetails.type === "Shuttie2") && (
                <>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Last Cleaning Cycle
                    </TableCell>
                    <TableCell data-th="Last Cleaning Cycle">
                      {lastCleaningCycle && lastCleaningCycle.lastCleaning && cleaningCycleInfo ? (
                        <Moment 
                          format="YYYY-MM-DD HH:mm:ss Z"
                          style={{ color: cleaningCycleInfo.isOlderThanTwoWeeks ? 'red' : 'inherit' }}
                        >
                          {lastCleaningCycle.lastCleaning}
                        </Moment>
                      ) : (
                        <div style={{ color: 'red' }}>Not Found</div>
                      )}
                    </TableCell>
                  </TableRow>
                  
                  {cleaningCycleInfo.shouldShowRequest && (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Last Cleaning Request
                      </TableCell>
                      <TableCell data-th="Last Cleaning Request">
                        {lastCleaningCycle.lastRequest ? (
                          <Moment 
                            format="YYYY-MM-DD HH:mm:ss Z"
                            style={{ color: 'red' }}
                          >
                            {lastCleaningCycle.lastRequest}
                          </Moment>
                        ) : (
                          <div style={{ color: 'red' }}>Not Found</div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}

              <TableRow>
                <TableCell component="th" scope="row">
                  Configuration Required
                </TableCell>
                <TableCell data-th="Configuration Required">
                  {sensorDetails.configurationRequired ? "Yes" : "No"}
                </TableCell>
              </TableRow>
            </TableBody>
            {customDetailsContainer}
          </Table>
        </Paper>
      );
      if (
        sensorDetails.isPairedTo != null &&
        sensorDetails.isPairedTo.length > 0
      ) {
        isPairedToContainer = (
          <Paper>
            <Table classes={{ root: "reactivetable" }}>
              <TableBody>
                {sensorDetails.isPairedTo.map((core,i) => (
                  <TableRow key={i+"tr"}>
                    <TableCell key={i+"tc1"} align={"center"}>
                      <NavLink
                        key={core}
                        to={"/device/" + core}
                        className="Nav_link"
                      >
                        {core}
                      </NavLink>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        );
      }
      return (
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent={"space-evenly"}>
            <Grid item xs={12} md={6} lg={5} xl={4}>
              <h4>Details</h4>
              {sensorDetailsContainer}
            </Grid>
            <Grid item xs={12} md={6} lg={5} xl={4}>
              <h4>Location Details</h4>
              <LocationDetails locationId={sensorDetails.location_id} />
              <h4>User Details</h4>
              <UserDetails userId={sensorDetails.owner} />
            </Grid>
          </Grid>
          <Grid container spacing={3} justifyContent={"space-evenly"}>
            <Grid item xs={12} md={12} lg={8} xl={6}>
              <h4>Devices on Location</h4>
              <DevicesOnLocation
                locationId={sensorDetails.location_id}
                userId={sensorDetails.owner}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4} xl={3}>
              {isPairedToContainer ? (
                <Fragment>
                  <h4>Paired Devices</h4>
                  {isPairedToContainer}
                </Fragment>
              ) : null}
            </Grid>
          </Grid>
        </Container>
      );
    }
  } else {
    return (
    <Paper>
       <div>Trouble Fetching Location Details</div>
      </Paper>
  );
  }
}

export default SensorDetails;
